import { AlertCircle } from 'lucide-react';
import { cn } from 'src/lib/utils';
import { feedbackDs } from './feedback-ds-classes';

interface FeedbackDsFieldProps {
  label: string;
  htmlFor?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
}

export function FeedbackDsField({
  label,
  htmlFor,
  hint,
  error,
  required = false,
  children,
}: FeedbackDsFieldProps) {
  return (
    <div className={feedbackDs.field}>
      <label
        htmlFor={htmlFor}
        className={feedbackDs.label}
      >
        {label}
        {required ? (
          <span className="ml-0.5 text-[#DC2626]">
            *
          </span>
        ) : null}
      </label>
      {children}
      {error ? (
        <p
          className={feedbackDs.error}
          role="alert"
        >
          <AlertCircle
            className="size-3 shrink-0"
            aria-hidden
          />
          {error}
        </p>
      ) : null}
      {!error && hint ? (
        <p className={feedbackDs.hint}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

interface FeedbackDsInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
}

export function FeedbackDsInput({ error, className, ...props }: FeedbackDsInputProps) {
  return (
    <input
      className={cn(feedbackDs.input, error && feedbackDs.inputError, className)}
      {...props}
    />
  );
}

interface FeedbackDsTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean;
}

export function FeedbackDsTextarea({ error, className, ...props }: FeedbackDsTextareaProps) {
  return (
    <textarea
      className={cn(feedbackDs.textarea, error && feedbackDs.inputError, className)}
      {...props}
    />
  );
}

interface FeedbackDsSegmentOption<T extends string> {
  value: T;
  label: string;
}

interface FeedbackDsSegmentedControlProps<T extends string> {
  options: FeedbackDsSegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  name: string;
}

export function FeedbackDsSegmentedControl<T extends string>({
  options,
  value,
  onChange,
  name,
}: FeedbackDsSegmentedControlProps<T>) {
  return (
    <div
      className={feedbackDs.segmentGroup}
      role="group"
      aria-label={name}
    >
      {options.map((option) => {
        const isActive = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            className={cn(feedbackDs.segment, isActive && feedbackDs.segmentActive)}
            aria-pressed={isActive}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

interface FeedbackDsFileUploadProps {
  id: string;
  inputRef: React.RefObject<HTMLInputElement | null>;
  fileName: string | null;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
}

export function FeedbackDsFileUpload({
  id,
  inputRef,
  fileName,
  onChange,
  disabled = false,
}: FeedbackDsFileUploadProps) {
  return (
    <div className="space-y-2">
      <input
        id={id}
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={onChange}
        disabled={disabled}
        className="sr-only"
      />
      <label
        htmlFor={id}
        className={cn(feedbackDs.fileButton, disabled && 'pointer-events-none opacity-45')}
      >
        Choose image
      </label>
      <p className={feedbackDs.hint}>
        {fileName
          ? `Selected: ${fileName}`
          : 'A screenshot of the current page is captured automatically if you do not upload one.'}
      </p>
    </div>
  );
}
